"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Sparkles, Droplets, Coffee, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const ritualSteps = [{
  step: "Paso 01",
  phase: "Rostro",
  product: "Crema facial Maitake",
  description: "Nutre y protege la barrera de tu piel cada mañana con extracto puro de Maitake, devolviendo suavidad y luminosidad natural.",
  icon: Sparkles,
  image: {
    src: "https://spora.beauty/wp-content/uploads/2026/09/la2.png",
    srcset: "https://spora.beauty/wp-content/uploads/2026/09/la2.png",
    sizes: "https://spora.beauty/wp-content/uploads/2026/09/la2.png",
    alt: "Aplicación de crema facial Maitake en el rostro"
  }
}, {
  step: "Paso 02",
  phase: "Cuerpo",
  product: "Crema corporal Reishi",
  description: "Aporta hidratación profunda y una textura sedosa tras el baño, aprovechando las propiedades reconfortantes del Reishi.",
  icon: Droplets,
  image: {
    src: "https://spora.beauty/wp-content/uploads/2026/09/la-1.png",
    srcset: "https://spora.beauty/wp-content/uploads/2026/09/la-1.png",
    sizes: "https://spora.beauty/wp-content/uploads/2026/09/la-1.png",
    alt: "Cuidado corporal con crema de extracto de hongo Reishi"
  }
}, {
  step: "Paso 03",
  phase: "Interior",
  product: "Té de Shiitake y Tintura de Hongos",
  description: "Una infusión herbal y gotas botánicas para comenzar o pausar el día, cultivando equilibrio y vitalidad desde adentro.",
  icon: Coffee,
  image: {
    src: "https://spora.beauty/wp-content/uploads/2026/09/la3.png",
    srcset: "https://spora.beauty/wp-content/uploads/2026/09/la3.png",
    sizes: "https://spora.beauty/wp-content/uploads/2026/09/la3.png",
    alt: "Preparación de té de hierbas e infusión de Shiitake"
  }
}];
export default function Ritual() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  return <section id="el-ritual" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-5 sm:px-8 lg:px-14 max-w-[1240px]">
        {/* Section Header */}
        <div className="max-w-2xl mx-auto text-center mb-12 md:mb-16">
          <span className="inline-block text-xs font-semibold uppercase tracking-widest text-primary bg-accent/60 px-3.5 py-1 rounded-full mb-3">
            El ritual diario
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-foreground mb-4">
            Tres pasos para cuidar piel e interior
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Un método pausado con hongos funcionales que nutre tu piel por fuera y acompaña tu bienestar por dentro.
          </p>
        </div>

        {/* 3-Step Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {ritualSteps.map((item, index) => {
          const IconComponent = item.icon;
          return <motion.article key={item.phase} variants={cardVariants} data-index={index} className="bg-card text-card-foreground rounded-2xl border border-border overflow-hidden flex flex-col justify-between shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-300">
                <div>
                  {/* Card Image */}
                  <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
                    <img src={item.image.src} data-wvc-srcset={item.image.srcset} data-wvc-sizes={item.image.sizes} alt={item.image.alt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    <div className="absolute top-3 left-3 bg-card/90 backdrop-blur-xs text-foreground px-2.5 py-1 rounded-full text-xs font-medium border border-border/80">
                      {item.step}
                    </div>
                  </div>

                  {/* Card Details */}
                  <div className="p-6 md:p-7">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="size-10 rounded-full bg-accent/80 text-accent-foreground flex items-center justify-center shrink-0">
                        <IconComponent className="size-5" />
                      </div>
                      <div>
                        <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold block">
                          {item.phase}
                        </span>
                        <h3 className="font-serif text-xl font-semibold text-card-foreground leading-tight">
                          {item.product}
                        </h3>
                      </div>
                    </div>

                    <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>

                {/* Subtle Card Footer Indicator */}
                <div className="px-6 pb-6 pt-0">
                  <div className="w-full h-px bg-border/80 mb-3" />
                  <span className="text-xs font-medium text-primary flex items-center gap-1.5">
                    Integrar al ritual diario
                  </span>
                </div>
              </motion.article>;
        })}
        </motion.div>

        {/* Section Action / Secondary Link */}
        <div className="mt-12 md:mt-16 text-center">
          <Button asChild size="lg" className="rounded-full px-8 font-semibold">
            <Link to="/ShopPage">
              Ver el kit completo
              <ArrowRight className="size-4 ml-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}